import type {
  AgentWorkbenchQuickStartCategory,
  AgentWorkbenchQuickStartIcon,
  AgentWorkbenchQuickStartTemplate,
  AgentWorkbenchQuickStartViewModel,
} from '@inf-monkeys-tech/monkeys';
import { ArrowLeft, ArrowUpRight, Code2, FileText, Palette, Search, ShoppingBag, Sparkles, Upload, type LucideIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useMonkeysLocaleMessages } from '../../provider';
import { cn } from '../layout';

export type {
  AgentWorkbenchQuickStartCategory,
  AgentWorkbenchQuickStartIcon,
  AgentWorkbenchQuickStartSection,
  AgentWorkbenchQuickStartTemplate,
  AgentWorkbenchQuickStartViewModel,
} from '@inf-monkeys-tech/monkeys';

export type AgentWorkbenchQuickStartIntent =
  | { type: 'change-category'; categoryId?: string }
  | { type: 'change-section'; categoryId: string; sectionId: string }
  | {
      type: 'select-template';
      templateId: string;
      prompt: string;
      files?: File[];
    };

export interface AgentWorkbenchQuickStartProps {
  viewModel: AgentWorkbenchQuickStartViewModel;
  className?: string;
  onIntent?: (intent: AgentWorkbenchQuickStartIntent) => void;
}

const ICONS: Record<AgentWorkbenchQuickStartIcon, LucideIcon> = {
  sparkles: Sparkles,
  palette: Palette,
  search: Search,
  'file-text': FileText,
  code: Code2,
  'shopping-bag': ShoppingBag,
};

function TemplateVisual({ template }: { template: AgentWorkbenchQuickStartTemplate }) {
  const Icon = ICONS[template.icon || 'sparkles'];

  if (template.coverImageUrl) {
    return <img src={template.coverImageUrl} alt="" className="size-full bg-muted object-contain" />;
  }

  return (
    <span className="flex size-full items-center justify-center bg-gradient-to-br from-primary/5 via-muted/50 to-primary/15">
      <span className="flex size-14 items-center justify-center rounded-2xl border border-primary/10 bg-background/85 text-primary shadow-sm">
        <Icon className="size-7" aria-hidden="true" />
      </span>
    </span>
  );
}

export function AgentWorkbenchQuickStart({ viewModel, className, onIntent }: AgentWorkbenchQuickStartProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.quickStart;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string>();
  const [activeSectionId, setActiveSectionId] = useState<string>();
  const [pendingTemplate, setPendingTemplate] = useState<AgentWorkbenchQuickStartTemplate>();
  const categories = useMemo(
    () =>
      viewModel.categories
        .map((category) => ({
          ...category,
          sections: category.sections
            .map((section) => ({
              ...section,
              templates: section.templates.filter((template) => !template.modes || template.modes.includes(viewModel.mode)),
            }))
            .filter((section) => section.templates.length > 0),
        }))
        .filter((category) => category.sections.length > 0),
    [viewModel.categories, viewModel.mode],
  );
  const activeCategory = categories.find((category) => category.id === activeCategoryId);
  const activeSection = activeCategory?.sections.find((section) => section.id === activeSectionId) || activeCategory?.sections[0];
  const usesCompactList = activeSection?.templates.length
    ? activeSection.templates.every((template) => !template.coverImageUrl)
    : false;

  useEffect(() => {
    if (activeCategoryId && !categories.some((category) => category.id === activeCategoryId)) {
      setActiveCategoryId(undefined);
      setActiveSectionId(undefined);
    }
  }, [activeCategoryId, categories]);

  if (categories.length === 0) return null;

  const selectCategory = (category: AgentWorkbenchQuickStartCategory) => {
    setActiveCategoryId(category.id);
    setActiveSectionId(category.sections[0]?.id);
    onIntent?.({ type: 'change-category', categoryId: category.id });
  };

  const selectSection = (sectionId: string) => {
    if (!activeCategory) return;
    setActiveSectionId(sectionId);
    onIntent?.({
      type: 'change-section',
      categoryId: activeCategory.id,
      sectionId,
    });
  };

  const selectTemplate = (template: AgentWorkbenchQuickStartTemplate) => {
    if (viewModel.disabled) return;
    if (template.action !== 'upload') {
      onIntent?.({
        type: 'select-template',
        templateId: template.id,
        prompt: template.prompt,
      });
      return;
    }

    const input = fileInputRef.current;
    if (!input) return;
    input.accept = template.accept || '*/*';
    input.multiple = template.multiple === true;
    setPendingTemplate(template);
    input.click();
  };

  return (
    <section className={cn('agent-workbench-quick-start min-w-0 max-w-full', className)} aria-label={messages.label}>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={(event) => {
          const files = Array.from(event.target.files || []);
          if (pendingTemplate && files.length > 0) {
            onIntent?.({
              type: 'select-template',
              templateId: pendingTemplate.id,
              prompt: pendingTemplate.prompt,
              files: pendingTemplate.multiple ? files : files.slice(0, 1),
            });
          }
          setPendingTemplate(undefined);
          event.target.value = '';
        }}
      />

      {!activeCategory ? (
        <div className="flex max-w-full items-center justify-center gap-2 overflow-x-auto px-1 pb-1">
          {categories.map((category) => {
            const Icon = ICONS[category.icon || 'sparkles'];
            return (
              <button key={category.id} type="button" className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-border bg-background px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" onClick={() => selectCategory(category)}>
                <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
                {category.label}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex min-w-0 max-w-full items-center gap-1 overflow-x-auto pb-1">
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => {
                setActiveCategoryId(undefined);
                setActiveSectionId(undefined);
                onIntent?.({ type: 'change-category' });
              }}
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              {messages.back}
            </button>
            {activeCategory.sections.map((section) => (
              <button key={section.id} type="button" aria-pressed={activeSection?.id === section.id} className={cn('h-9 shrink-0 rounded-full px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', activeSection?.id === section.id ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground')} onClick={() => selectSection(section.id)}>
                {section.label}
              </button>
            ))}
          </div>

          <div
            data-quick-start-layout={usesCompactList ? 'list' : 'cards'}
            className={cn(
              'grid',
              usesCompactList ? 'grid-cols-1 gap-0' : 'grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-3',
            )}
          >
            {activeSection?.templates.map((template) => (
              <button
                key={template.id}
                type="button"
                disabled={viewModel.disabled}
                data-quick-start-template-id={template.id}
                className={cn(
                  'group min-w-0 overflow-hidden text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
                  usesCompactList
                    ? 'flex min-h-14 items-center rounded-none border-x-0 border-t-0 bg-transparent shadow-none hover:border-border hover:bg-muted/40 last:border-b-0'
                    : 'rounded-xl border border-border bg-background hover:border-primary/30 hover:bg-muted/20',
                )}
                onClick={() => selectTemplate(template)}
              >
                <span className={cn(usesCompactList ? 'min-w-0 flex-1 px-2 py-2.5' : 'block px-4 pb-3 pt-4')}>
                  <span className={cn('flex items-center gap-2 text-sm text-foreground', usesCompactList ? 'font-medium' : 'font-semibold')}>
                    {template.title}
                    {template.action === 'upload' ? (
                      <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground">
                        <Upload className="size-3" aria-hidden="true" />
                        {messages.addFile}
                      </span>
                    ) : null}
                  </span>
                  {template.description ? (
                    <span
                      className={cn(
                        'line-clamp-2 block text-xs leading-4 text-muted-foreground',
                        usesCompactList ? 'mt-0.5 min-h-0 text-muted-foreground/75' : 'mt-1 min-h-8',
                      )}
                    >
                      {template.description}
                    </span>
                  ) : null}
                </span>
                {usesCompactList ? (
                  <ArrowUpRight className="mr-2 size-4 shrink-0 text-muted-foreground/45" aria-hidden="true" />
                ) : (
                  <span className={cn('block overflow-hidden', template.coverImageUrl ? 'aspect-[5/2]' : 'h-28')}>
                    <TemplateVisual template={template} />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
